/**
 * Wind-related utility functions for converting and formatting wind data
 */

/**
 * Converts wind direction in degrees to cardinal direction
 * @param degrees - Wind direction in degrees (0-360)
 * @returns Cardinal direction (N, NE, E, SE, S, SW, W, NW)
 */
export const degreesToCardinal = (degrees: number | null | undefined): string => {
  if (degrees === null || degrees === undefined || isNaN(degrees)) {
    return "";
  }

  // Normalize degrees to 0-360 range
  const normalized = ((degrees % 360) + 360) % 360;

  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const index = Math.round(normalized / 45) % 8;
  return directions[index];
};

/**
 * Converts meters per second to miles per hour
 * @param mps - Wind speed in meters per second
 * @returns Wind speed in miles per hour
 */
export const metersPerSecondToMph = (mps: number | null | undefined): number | null => {
  if (mps === null || mps === undefined || isNaN(mps)) {
    return null;
  }
  return mps * 2.23694;
};

/**
 * Converts meters per second to kilometers per hour
 * @param mps - Wind speed in meters per second
 * @returns Wind speed in kilometers per hour
 */
export const metersPerSecondToKmh = (mps: number | null | undefined): number | null => {
  if (mps === null || mps === undefined || isNaN(mps)) {
    return null;
  }
  return mps * 3.6;
};

/**
 * Formats wind speed with appropriate units
 * @param mps - Wind speed in meters per second
 * @param useMetric - Whether to use metric units (km/h) or imperial (mph)
 * @returns Formatted wind speed string (e.g., "10 mph" or "16 km/h")
 */
export const formatWindSpeed = (
  mps: number | null | undefined,
  useMetric: boolean = false
): string => {
  if (mps === null || mps === undefined || isNaN(mps)) {
    return "--";
  }

  if (useMetric) {
    const kmh = metersPerSecondToKmh(mps);
    if (kmh === null) return "--";
    const rounded = Math.round(kmh);
    return rounded === 0 ? "< 2 km/h" : `${rounded} km/h`;
  } else {
    const mph = metersPerSecondToMph(mps);
    if (mph === null) return "--";
    const rounded = Math.round(mph);
    return rounded === 0 ? "< 1 mph" : `${rounded} mph`;
  }
};

/**
 * Formats wind direction as degrees and cardinal direction
 * @param degrees - Wind direction in degrees (0-360)
 * @returns Formatted wind direction string (e.g., "270° W")
 */
export const formatWindDirection = (degrees: number | null | undefined): string => {
  if (degrees === null || degrees === undefined || isNaN(degrees)) {
    return "--";
  }

  const cardinal = degreesToCardinal(degrees);
  const normalized = Math.round(((degrees % 360) + 360) % 360);
  return `${normalized}° ${cardinal}`;
};

/**
 * Formats complete wind information (speed and direction)
 * @param speedMps - Wind speed in meters per second
 * @param directionDegrees - Wind direction in degrees
 * @param useMetric - Whether to use metric units
 * @returns Formatted wind string (e.g., "10 mph 270° W" or "16 km/h 270° W")
 */
export const formatWindInfo = (
  speedMps: number | null | undefined,
  directionDegrees: number | null | undefined,
  useMetric: boolean = false
): string => {
  const speed = formatWindSpeed(speedMps, useMetric);
  const direction = formatWindDirection(directionDegrees);

  // If both are missing, show N/A instead of --
  if (speed === "--" && direction === "--") {
    return "N/A";
  }
  if (speed === "--") {
    return direction;
  }
  if (direction === "--") {
    return speed;
  }

  return `${speed} ${direction}`;
};
